import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import CollectionCard from "@/components/CollectionCard";
import { Search, ArrowRight, Sparkles } from "lucide-react";

export default function Home() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/public/collections").then((r) => setCollections(r.data)).finally(() => setLoading(false));
  }, []);

  const goSearch = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="min-h-screen bg-parchment grain">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-stoneline">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1550642249-6e5605421172?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Nepal heritage"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/70 to-ink/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 text-gold text-xs font-semibold uppercase tracking-[0.25em]">
              <Sparkles className="h-4 w-4" /> Digital Collections
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-parchment tracking-tight leading-[1.05]">
              Curate the visual history of Nepal
            </h1>
            <p className="mt-5 text-lg text-parchment/80 leading-relaxed">
              Search over 32,000 archival photographs and curate your own collections highlighting
              Nepal's history and rich heritage. Submit them for review and share published collections with the world.
            </p>

            <form onSubmit={goSearch} className="mt-8 flex gap-2 max-w-lg">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                <input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search the archive — e.g. Rana, Kathmandu, temples…"
                  className="w-full pl-11 pr-4 py-3.5 rounded-full bg-parchment/95 text-ink placeholder:text-stone-400 outline-none focus:ring-2 focus:ring-gold"
                />
              </div>
              <button
                data-testid="hero-search-btn"
                type="submit"
                className="bg-brick hover:bg-brick-dark text-parchment px-6 rounded-full font-semibold transition-colors flex items-center gap-2"
              >
                Search
              </button>
            </form>

            {!user && (
              <button
                data-testid="hero-signin-btn"
                onClick={login}
                className="mt-5 text-parchment/90 text-sm font-medium inline-flex items-center gap-1.5 hover:text-gold transition-colors"
              >
                Sign in to start curating <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Published collections */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-ink">Published Collections</h2>
            <p className="text-muted-foreground mt-1">Curated digital galleries approved by Archive Nepal.</p>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] rounded-xl bg-stone-200/60 animate-pulse" />
            ))}
          </div>
        ) : collections.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-stoneline rounded-xl bg-white/50">
            <p className="font-serif text-xl text-ink">No published collections yet</p>
            <p className="text-muted-foreground mt-2">Be the first to curate and submit one for review.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {collections.map((c) => (
              <CollectionCard key={c.id} collection={c} to={`/c/${c.id}`} showOwnerLink />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
